import {
  createAction,
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { debounce } from "lodash-es";

import { WebviewApi } from "@/features/webview_api";
import type { Building, Floor, Poi, PoiCategory } from "../../libs/models";
import { safeLocalStorage } from "../utils/utils";

const MAX_ELEMENTS_PER_BUILDING = 5;

export enum HistoryType {
  SEARCH,
  POI_CLICK,
}

export enum BuildingState {
  COMPLETE,
  PARTIAL,
  PREFETCHED,
}

type BaseHistoryElement = {
  text: string;
  buildingId?: number;
  type: HistoryType;
};

type SearchHistoryElement = BaseHistoryElement & {
  type: HistoryType.SEARCH;
};

type PoiHistoryElement = BaseHistoryElement & {
  type: HistoryType.POI_CLICK;
  poiId: number;
};

export interface BuildingElement {
  timestap: number;
  state: BuildingState;
  value: Building;
}

interface BuildingStoreElement {
  [key: number]: BuildingElement;
}

export type HistoryElement = SearchHistoryElement | PoiHistoryElement;

export interface SliceProps {
  buildings: BuildingStoreElement;
  currentBuildingID: number | null;
  currentFloorID: number | null;
  currentPoiID: number | null;
  currentFeatureID: string | null;
  currentPoiCategoriesIDs: Array<PoiCategory["id"]> | null;
  currentFilterText: string;
  searchHistory: Array<HistoryElement>;
  favoritePois: number[];
  visiblePoisFilter: number[];
}

const initialState: SliceProps = {
  buildings: {},
  currentBuildingID: null,
  currentFeatureID: null,
  currentFilterText: "",
  currentFloorID: null,
  currentPoiCategoriesIDs: null,
  currentPoiID: null,
  favoritePois: (safeLocalStorage.getItem("favorite-pois") &&
  Array.isArray(JSON.parse(safeLocalStorage.getItem("favorite-pois") || ""))
    ? JSON.parse(safeLocalStorage.getItem("favorite-pois") || "")
    : []) as number[],
  searchHistory: (safeLocalStorage.getItem("poi-search-history") &&
  Array.isArray(
    JSON.parse(safeLocalStorage.getItem("poi-search-history") || ""),
  )
    ? JSON.parse(safeLocalStorage.getItem("poi-search-history") || "")
    : []) as HistoryElement[],
  visiblePoisFilter: [],
};

export const setBuilding = createAction<BuildingElement>("setBuilding");

export const CartographySlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(
      setBuilding,
      (state, action: PayloadAction<BuildingElement>) => {
        if (action.payload.value == null) return;

        state.buildings[action.payload.value.id] = action.payload;
      },
    );
  },
  initialState,
  name: "cartography",
  reducers: {
    appendBuildings: (state, action: PayloadAction<BuildingElement[]>) => {
      const buildingState = state.buildings;
      action.payload.forEach((b) => (buildingState[b.value.id] = b));
      state.buildings = buildingState;
    },
    changeBuilding: (state, action: PayloadAction<number>) => {
      if (state.currentBuildingID === action.payload) return;
      state.currentBuildingID = action.payload;

      WebviewApi.onBuildingSelected(action.payload);

      state.currentFloorID =
        state.buildings[state.currentBuildingID]?.value?.getFirstFloor()?.id ||
        null;

      if (state.currentFloorID != null && state.currentBuildingID != null) {
        WebviewApi.onFloorSelected(
          state.currentFloorID,
          state.currentBuildingID,
        );
      }
    },
    pushElementToHistory: (state, action: PayloadAction<HistoryElement>) => {
      // if not stored yet
      if (
        !state.searchHistory.find(
          (e) =>
            e.text.toLocaleLowerCase() ===
              action.payload.text.trimEnd().toLocaleLowerCase() &&
            e.buildingId === action.payload.buildingId,
        )
      ) {
        // check any stored value contains our current value
        const ignorable = state.searchHistory.find(
          (e) =>
            e.text
              .toLocaleLowerCase()
              .includes(action.payload.text.trimEnd().toLocaleLowerCase()) &&
            e.buildingId === action.payload.buildingId,
        );
        // ignore it
        if (ignorable) return;

        // check our current value contains a stored value
        const substringFound = state.searchHistory.find(
          (e) =>
            action.payload.text
              .trimEnd()
              .toLocaleLowerCase()
              .includes(e.text.toLocaleLowerCase()) &&
            e.buildingId === action.payload.buildingId,
        );
        //replace less significant string
        if (substringFound)
          state.searchHistory = state.searchHistory.filter(
            (e) => substringFound !== e,
          );
        state.searchHistory.unshift(action.payload);
      } else {
        // if match with already stored value move it to top
        state.searchHistory = state.searchHistory.filter(
          (e) =>
            e.text.toLocaleLowerCase() !==
              action.payload.text.trimEnd().toLocaleLowerCase() &&
            e.buildingId === action.payload.buildingId,
        );
        state.searchHistory.unshift(action.payload);
      }

      // keep history length to 5 elements for each building ID
      let i = 0;
      state.searchHistory = state.searchHistory.filter((el: HistoryElement) => {
        if (el.buildingId !== action.payload.buildingId) return el;
        i++;
        if (i <= MAX_ELEMENTS_PER_BUILDING) return el;
      });

      // save in local storage
      safeLocalStorage.setItem("poi-search-history", state.searchHistory);
    },
    removeElementFromHistory: (state, action: PayloadAction<string>) => {
      state.searchHistory = state.searchHistory.filter(
        (e) => e.text !== action.payload,
      );
      safeLocalStorage.setItem("poi-search-history", state.searchHistory);
    },
    resetSearch: (state, _action: PayloadAction<void>) => {
      state.currentFilterText = "";
      state.currentPoiCategoriesIDs = null;
    },
    setBuildings: (state, action: PayloadAction<BuildingElement[]>) => {
      const buildingState: BuildingStoreElement = {};
      action.payload.forEach((b) => (buildingState[b.value.id] = b));
      state.buildings = buildingState;
    },
    setCurrentBuildingID: (
      state,
      action: PayloadAction<Building["id"] | null>,
    ) => {
      if (state.currentBuildingID === action.payload) return;
      state.currentBuildingID = action.payload;
      WebviewApi.onBuildingSelected(action.payload);
    },
    setCurrentBuildingIMDF: (
      state,
      action: PayloadAction<Building["geojson"]>,
    ) => {
      if (state.currentBuildingID) {
        const newBuilding = state.buildings[state.currentBuildingID].value.copy(
          { geojson: action.payload.clone() },
        );

        state.buildings = {
          ...state.buildings,
          [state.currentBuildingID]: {
            ...state.buildings[state.currentBuildingID],
            timestap: Date.now(),
            value: newBuilding,
          },
        };
      }
    },
    setCurrentFeatureID: (state, action: PayloadAction<string | null>) => {
      if (state.currentFeatureID === action.payload) return;
      state.currentFeatureID = action.payload;
    },
    setCurrentFilterText: (
      state,
      action: PayloadAction<SliceProps["currentFilterText"]>,
    ) => {
      state.currentFilterText = action.payload;
    },
    setCurrentFloorID: (state, action: PayloadAction<Floor["id"] | null>) => {
      if (state.currentFloorID === action.payload) {
        return;
      }
      state.currentFloorID = action.payload;

      if (state.currentFloorID != null && state.currentBuildingID != null) {
        WebviewApi.onFloorSelected(
          state.currentFloorID,
          state.currentBuildingID,
        );
      }
    },
    setCurrentPoiCategoriesIDs: (
      state,
      action: PayloadAction<Array<PoiCategory["id"]> | null>,
    ) => {
      if (action?.payload?.length === 0 || action?.payload == null) {
        state.currentPoiCategoriesIDs !== null &&
          WebviewApi.onPoiCategoryDeselected(state.currentPoiCategoriesIDs);
        state.currentPoiCategoriesIDs = null;
      } else {
        WebviewApi.onPoiCategorySelected(action.payload);
        state.currentPoiCategoriesIDs = action.payload;
      }
    },
    setCurrentPoiID: (state, action: PayloadAction<Poi["id"] | null>) => {
      if (state.currentPoiID === action.payload) {
        return;
      }

      if (state.currentPoiID != null && state.currentBuildingID != null) {
        WebviewApi.onPoiDeselected(state.currentPoiID, state.currentBuildingID);
      }
      if (action.payload != null && state.currentBuildingID != null) {
        WebviewApi.onPoiSelected(action.payload, state.currentBuildingID);
      }

      state.currentPoiID = action.payload || null;
      let floorID: Floor["id"] | null = null;
      let buildingID: Building["id"] | null = null;

      Object.values(state.buildings)
        .filter((building) => building.value?.pois?.length() > 0)
        .map((building) => building.value)
        .forEach((building) =>
          building.pois?.toArray().forEach((poi) => {
            if (poi.id === action.payload) {
              floorID = poi.position.floor_id as Floor["id"];
              buildingID = poi.buildingId;
            }
          }),
        );

      floorID &&
        state.currentFloorID !== floorID &&
        state.buildings[state.currentBuildingID]?.value?.getFloorById(
          floorID,
        ) &&
        (state.currentFloorID = floorID || null);

      buildingID &&
        state.currentBuildingID !== buildingID &&
        (state.currentBuildingID = buildingID || null);
    },
    // used in user interaction
    setFavoritePois: (state, action: PayloadAction<number[]>) => {
      state.favoritePois = action.payload;
      safeLocalStorage.setItem("favorite-pois", state.favoritePois); // local cache

      const currBuildingPois = state.currentBuildingID
        ? state.buildings[state.currentBuildingID]?.value?.pois
        : null;

      // notify just current building pois
      const favoritePOIsCurrentBuilding = currBuildingPois
        ?.getPoisByIds(action.payload)
        .map((p) => p.id as number);

      WebviewApi.favoritePoisUpdated(favoritePOIsCurrentBuilding || []); // notify plugins
    },
    // used in Postmessage API
    setFavoritePoisFilteringByBuilding: (
      state,
      action: PayloadAction<number[]>,
    ) => {
      const currentBuildingPois = state.currentBuildingID
        ? state.buildings[state.currentBuildingID]?.value?.pois
        : null;

      //filter non valid pois
      const filteredNewPoisToFav = currentBuildingPois
        ?.getPoisByIds(action.payload)
        .map((p) => p.id as number);

      if (
        !filteredNewPoisToFav ||
        (action.payload.length !== 0 && filteredNewPoisToFav.length === 0) // avoid cleaning fav pois when just invalid values are passed as input
      ) {
        const currentBuildingFavoritePois = currentBuildingPois
          ?.getPoisByIds(state.favoritePois)
          .map((p) => p.id as number);
        WebviewApi.favoritePoisUpdated(currentBuildingFavoritePois || []); // notify plugins anyway
        return;
      }

      // instead of overriding all favorite pois just override the current building ones keeping other buildings'
      const otherBuildingsFavoritePOIs = state.favoritePois.filter(
        (poiId) => !currentBuildingPois?.getPoiById(poiId),
      );

      WebviewApi.favoritePoisUpdated(filteredNewPoisToFav); // notify just current building ones, doing it before pushing other buildings' ones
      state.favoritePois = filteredNewPoisToFav;
      otherBuildingsFavoritePOIs &&
        state.favoritePois.push(...otherBuildingsFavoritePOIs);

      safeLocalStorage.setItem("favorite-pois", state.favoritePois); // local cache
    },
    setVisiblePoisFilter: (state, action: PayloadAction<number[]>) => {
      state.visiblePoisFilter = action.payload;
    },
    unsetCurrentPoiID: (state, _action: PayloadAction<void>) => {
      if (state.currentPoiID != null && state.currentBuildingID != null) {
        WebviewApi.onPoiDeselected(state.currentPoiID, state.currentBuildingID);
      }
      state.currentPoiID = null;
      state.currentFeatureID = null;
    },
  },
});

export const setDebouncedCurrentFloorID = createAsyncThunk(
  "cartography/currentFloorID",
  debounce(async (payload: SliceProps["currentFloorID"], { dispatch }) => {
    dispatch(setCurrentFloorID(payload));
  }, 200),
);

// Action creators are generated for each case reducer function
export const {
  appendBuildings,
  changeBuilding,
  pushElementToHistory,
  removeElementFromHistory,
  resetSearch,
  setBuildings,
  setCurrentBuildingID,
  setCurrentBuildingIMDF,
  setCurrentFeatureID,
  setCurrentFilterText,
  setCurrentFloorID,
  setCurrentPoiCategoriesIDs,
  setCurrentPoiID,
  setFavoritePois,
  setFavoritePoisFilteringByBuilding,
  setVisiblePoisFilter,
  unsetCurrentPoiID,
} = CartographySlice.actions;

export default CartographySlice.reducer;
